import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/SessionProvider";

const TERMS_VERSION = "1.0";

type TermsAcceptance = {
  user_type: "renter" | "host";
  version: string;
};

export function useTermsAcceptance() {
  const { user } = useSession();
  const queryClient = useQueryClient();

  // Hent eksisterende accepter
  const { data, isLoading } = useQuery({
    queryKey: ["terms-acceptance", user?.id],
    enabled: !!user,
    queryFn: async () => {
      console.log("[useTermsAcceptance] Fetching for user:", user?.id);
      if (!user) return { hasAcceptedRenter: false, hasAcceptedHost: false };

      const { data: acceptances, error } = await (supabase
        .from("terms_acceptances" as any)
        .select("user_type, version")
        .eq("user_id", user.id)
        .eq("version", TERMS_VERSION) as unknown as Promise<{ data: TermsAcceptance[] | null; error: any }>);

      if (error) {
        console.error("[useTermsAcceptance] Error fetching:", error);
        throw error;
      }

      const hasAcceptedRenter = acceptances?.some((a) => a.user_type === "renter") ?? false;
      const hasAcceptedHost = acceptances?.some((a) => a.user_type === "host") ?? false;

      console.log("[useTermsAcceptance] Result:", { hasAcceptedRenter, hasAcceptedHost, acceptances });
      return { hasAcceptedRenter, hasAcceptedHost };
    },
  });

  // Mutation til at acceptere vilkår
  const acceptMutation = useMutation({
    mutationFn: async (userType: "renter" | "host") => {
      console.log("[useTermsAcceptance] Accepting terms:", userType);
      if (!user) throw new Error("Du skal være logget ind");

      const { error } = await (supabase.from("terms_acceptances" as any).insert({
        user_id: user.id,
        user_type: userType,
        version: TERMS_VERSION,
      }) as unknown as Promise<{ error: any }>);

      if (error) {
        console.error("[useTermsAcceptance] Error accepting:", error);
        throw error;
      }
      console.log("[useTermsAcceptance] Accepted successfully:", userType);
    },
    onSuccess: () => {
      console.log("[useTermsAcceptance] Invalidating queries");
      queryClient.invalidateQueries({ queryKey: ["terms-acceptance", user?.id] });
    },
  });

  return {
    hasAcceptedRenter: data?.hasAcceptedRenter ?? false,
    hasAcceptedHost: data?.hasAcceptedHost ?? false,
    isLoading,
    acceptTerms: acceptMutation.mutateAsync,
    isAccepting: acceptMutation.isPending,
  };
}
